import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useCachedFetch } from '../../../../utils/cache';
import { useCart } from '../../../../context/CartContext';
import { ShoppingCart, LogIn, LayoutDashboard, LogOut, Wallet, ChevronDown, Menu, X } from 'lucide-react';
import AwpLogo from "../../../../Images/AwpLogo.png";
import MiniCart from '../../../../Components/MiniCart/MiniCart';
import ThemeToggle from '../../../../components/ThemeToggle/ThemeToggle';
import "./Header.css";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { getItemCount } = useCart();
  const userMenuRef = useRef(null);

  const { data: balanceData } = useCachedFetch(
    isAuthenticated ? '/api/withdrawals/balance' : null,
    { ttl: 2 * 60 * 1000, enabled: isAuthenticated }
  );
  const balance = balanceData?.balance ?? null;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
  ];

  const isLinkActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    navigate('/home');
  };

  const itemCount = getItemCount();
  const initial = (user?.username || 'U').slice(0, 1).toUpperCase();

  return (
    <header className="awp-header">
      <div className="awp-header-inner">
        {/* Left: Logo */}
        <Link to="/" className="awp-logo" aria-label="Home">
          <div className="awp-logo-branding">
            <span className="awp-logo-text">Get<span className="awp-logo-accent">Infinite</span></span>
            <span className="awp-logo-tagline">Grow Infinite. Earn Infinite.</span>
          </div>
        </Link>

        {/* Center: Nav */}
        <nav className="awp-nav">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`awp-nav-link ${isLinkActive(link.path) ? 'is-active' : ''}`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Right: Actions */}
        <div className="awp-actions">
          {isAuthenticated ? (
            <>
              {balance !== null && (
                <div className="awp-balance" title="Wallet balance">
                  <Wallet size={14} />
                  <span>Rs. {Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}

              <Link to="/dashboard" className="awp-btn-primary awp-dash-btn">
                <LayoutDashboard size={16} />
                <span>Dashboard</span>
              </Link>

              <div className="awp-user-menu" ref={userMenuRef}>
                <button
                  type="button"
                  className="awp-user-trigger"
                  onClick={() => setIsUserMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                >
                  <span className="awp-avatar">{initial}</span>
                  <ChevronDown size={14} className={`awp-chev ${isUserMenuOpen ? 'is-open' : ''}`} />
                </button>
                {isUserMenuOpen && (
                  <div className="awp-user-dropdown" role="menu">
                    <div className="awp-user-head">
                      <span className="awp-avatar awp-avatar-lg">{initial}</span>
                      <div>
                        <strong>{user?.username || 'User'}</strong>
                        {user?.email && <span>{user.email}</span>}
                      </div>
                    </div>
                    <Link to="/dashboard" className="awp-dropdown-item" role="menuitem">
                      <LayoutDashboard size={15} />
                      Dashboard
                    </Link>
                    <button type="button" className="awp-dropdown-item danger" onClick={handleLogout} role="menuitem">
                      <LogOut size={15} />
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link to="/login" className="awp-btn-primary">
              <LogIn size={16} />
              <span>Login</span>
            </Link>
          )}

          <ThemeToggle compact className="awp-header-theme" />

          <button
            type="button"
            className="awp-cart-btn"
            onClick={() => setIsCartOpen(true)}
            aria-label={`Cart with ${itemCount} item${itemCount === 1 ? '' : 's'}`}
          >
            <ShoppingCart size={18} />
            {itemCount > 0 && <span className="awp-cart-badge">{itemCount}</span>}
          </button>

          <button
            type="button"
            className="awp-menu-toggle"
            onClick={() => setIsMenuOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`awp-mobile-panel ${isMenuOpen ? 'is-open' : ''}`}>
        <nav className="awp-mobile-nav">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`awp-mobile-link ${isLinkActive(link.path) ? 'is-active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        <div className="awp-mobile-actions">
          {isAuthenticated ? (
            <>
              {balance !== null && (
                <div className="awp-balance awp-balance-block">
                  <Wallet size={14} />
                  <span>Rs. {Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              <Link to="/dashboard" className="awp-btn-primary awp-btn-block" onClick={() => setIsMenuOpen(false)}>
                <LayoutDashboard size={16} />
                <span>Dashboard</span>
              </Link>
              <button type="button" className="awp-btn-ghost awp-btn-block" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Log out</span>
              </button>
            </>
          ) : (
            <Link to="/login" className="awp-btn-primary awp-btn-block" onClick={() => setIsMenuOpen(false)}>
              <LogIn size={16} />
              <span>Login</span>
            </Link>
          )}
        </div>
      </div>

      <MiniCart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </header>
  );
};

export default Header;
